import { useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { Eyebrow, SectionHeader } from '@/components/Atoms';
import { BLOG_POSTS, BLOG_CATEGORIES, BLOG_TOPIC_IDEAS, getPostBySlug } from '@/data/blog';
import { BLOG as BLOG_TID } from '@/constants/testIds';
import { buildArticleSchema } from '@/data/schema';
import useSEO from '@/hooks/useSEO';

export function Blog() {
  const [filter, setFilter] = useState('All');
  const posts = filter === 'All' ? BLOG_POSTS : BLOG_POSTS.filter((p) => p.category === filter);

  useSEO({
    title: 'Operations Journal — Field Notes on Service Businesses',
    description:
      'Thoughtful articles on operations, business systems, customer experience, local SEO, and growth for service-based businesses.',
    path: '/blog',
  });

  return (
    <div data-testid={BLOG_TID.page}>
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl">
          <Eyebrow>The Operations Journal</Eyebrow>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1] tracking-tight mt-6">
            Thinking out loud<br /><span className="font-serif-italic italic text-[#C9A24D]">about service businesses.</span>
          </h1>
          <p className="mt-8 text-lg text-[#1C1C1C]/80 leading-relaxed">
            Field notes, frameworks, and quiet observations on operations, systems, customer service, SEO, marketing and the unglamorous craft of building a business that lasts.
          </p>
        </div>
      </section>

      <section className="pb-24 lg:pb-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <div className="flex flex-wrap gap-2 mb-12">
            {BLOG_CATEGORIES.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`${BLOG_TID.categoryFilter}-${c.toLowerCase().replace(/\s+/g, '-')}`}
                className={`text-xs uppercase tracking-[0.16em] px-4 py-2 border transition-colors ${
                  filter === c ? 'bg-[#1C1C1C] text-[#F4EFEA] border-[#1C1C1C]' : 'border-[#E6CFC7] text-[#1C1C1C] hover:border-[#C9A24D] hover:text-[#C9A24D]'
                }`}
              >
                {c}
              </button>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-10">
            {posts.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} data-testid={`${BLOG_TID.card}-${p.slug}`} className="group block">
                <div className="overflow-hidden aspect-[4/5]">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover editorial-img" />
                </div>
                <div className="eyebrow mt-6">{p.category}</div>
                <h3 className="font-serif-display text-2xl mt-3 leading-snug group-hover:text-[#C9A24D] transition-colors">{p.title}</h3>
                <p className="mt-3 text-sm text-[#1C1C1C]/70 leading-relaxed line-clamp-3">{p.excerpt}</p>
                <p className="mt-4 text-xs uppercase tracking-wider text-[#1C1C1C]/50">{p.date} &middot; {p.read}</p>
              </Link>
            ))}
            {posts.length === 0 && (
              <p className="text-[#1C1C1C]/60 font-serif-italic italic">No articles in this category yet.</p>
            )}
          </div>
        </div>
      </section>

      {/* Planned topics — pillar pages preview */}
      <section className="bg-[#FBF7F2] border-y border-[#E6CFC7]/70 py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow="Coming Up" title="Planned topics" italic="in the Journal." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-4">
            {BLOG_TOPIC_IDEAS.map((t, i) => (
              <div key={t} className="flex items-baseline gap-4 border-b border-[#E6CFC7]/60 py-3">
                <span className="text-xs uppercase tracking-[0.18em] text-[#C9A24D] w-8 shrink-0">{String(i + 1).padStart(2, '0')}</span>
                <span className="text-[#1C1C1C]/85 text-base">{t}</span>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const related = BLOG_POSTS.filter((p) => p.slug !== slug).slice(0, 2);

  useSEO({
    title: post ? `${post.title}` : 'Article',
    description: post?.excerpt,
    image: post?.image,
    path: `/blog/${slug}`,
    schema: post ? buildArticleSchema(post) : undefined,
  });

  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div data-testid={BLOG_TID.detail}>
      <article className="py-20 lg:py-28">
        <div className="max-w-[860px] mx-auto px-6 sm:px-10">
          <Eyebrow>{post.category}</Eyebrow>
          <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-[64px] leading-[1.04] tracking-tight mt-6">{post.title}</h1>
          <p className="mt-6 text-sm uppercase tracking-wider text-[#1C1C1C]/55">{post.date} &middot; {post.read}</p>
        </div>
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 mt-14">
          <div className="aspect-[16/9] overflow-hidden">
            <img src={post.image} alt={post.title} className="w-full h-full object-cover editorial-img" />
          </div>
        </div>
        <div className="max-w-[760px] mx-auto px-6 sm:px-10 mt-14 space-y-6 text-lg leading-relaxed text-[#1C1C1C]/85">
          {post.body.map((para, i) => (
            <p key={i} className={i === 0 ? 'drop-cap' : ''}>{para}</p>
          ))}
        </div>
      </article>

      <section className="bg-[#FBF7F2] border-t border-[#E6CFC7]/70 py-20 lg:py-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow="Keep reading" title="More from the" italic="journal." />
          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-10">
            {related.map((p) => (
              <Link key={p.slug} to={`/blog/${p.slug}`} className="group block">
                <div className="overflow-hidden aspect-[16/9]">
                  <img src={p.image} alt={p.title} className="w-full h-full object-cover editorial-img" />
                </div>
                <div className="eyebrow mt-6">{p.category}</div>
                <h3 className="font-serif-display text-2xl mt-3 group-hover:text-[#C9A24D] transition-colors">{p.title}</h3>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32 text-center">
        <div className="max-w-[1000px] mx-auto px-6">
          <h2 className="font-serif-display text-4xl sm:text-5xl leading-[1.05] tracking-tight">
            Ready to put<br/><span className="font-serif-italic italic text-[#C9A24D]">these ideas to work?</span>
          </h2>
          <div className="mt-10 flex flex-wrap justify-center gap-4">
            <Link to="/contact" className="btn-ghost-light" data-testid="blogpost-cta-book-call">Book a Discovery Call</Link>
            <Link to="/contact" className="text-sm uppercase tracking-[0.18em] text-[#F4EFEA]/70 hover:text-[#C9A24D] self-center transition-colors">Request a Consultation &rarr;</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
